import React, { memo, useMemo } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Tooltip } from "antd"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import IconWithBg from "ui/IconWithBg"
import { getJournalIcon } from "pagesV2/pro/Calendar/utils"
import { spacings } from "themes/variables"
import { PlusCircleOutlined } from "@ant-design/icons"
import { useDevice } from "hooks/useDevice"
import moment from "moment"
import styles from "../index.module.css"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"

const TimeOffTooltip = ({
  timeOffRanges = [],
  formattedDate,
  journalNoteDates,
  handleJournalModalOpen,
  handleAddBooking,
  day,
  renderList,
  onChangeFilters
}) => {
  const { t } = useTranslation()
  const { isMobile, isTablet } = useDevice()
  const { hasPermission } = usePermissions()
  const isPastDay = useMemo(() => day.isBefore(moment().startOf("day"), "day"), [day])

  const canViewJournal = hasPermission(MODULES.CALENDAR, ACTIONS.VIEW_JOURNAL)
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const canEditJournal = hasPermission(MODULES.CALENDAR, ACTIONS.EDIT_JOURNAL)
  const hasJournalNote = journalNoteDates?.includes(formattedDate)
  // Show icon if journal exists and user has view or edit permission, or if no journal and user has create permission
  const hasJournalPermission = hasJournalNote ? (canViewJournal || canEditJournal) : canCreateJournal

  if (!timeOffRanges?.length) return null

  return (
    <Tooltip
      placement="top"
      mouseEnterDelay={0.2}
      trigger={["hover", "focus"]}
      title={
        <div>
          {timeOffRanges.map((range) => (
            <div key={`${formattedDate}-${range}`}>
              {t("calendar.time_off")} {range}
            </div>
          ))}
        </div>
      }
    >
      <div className={`${styles.timeOffContainer} ${isPastDay ? styles.pastDay : ""}`} aria-disabled={isPastDay}>
        <Flex justify="center" align="center">
          <button
            type="button"
            aria-label={`${t("calendar.time_off")} ${timeOffRanges.join(", ")}`}
            className={`${styles.dateButton} cursor-pointer`}
          >
            <Text>{day.format("DD")}</Text>
          </button>
          {!isMobile && !isTablet && (
            <Flex className={styles.iconHover}>
              {hasJournalPermission && (
                <IconWithBg icon={getJournalIcon(journalNoteDates, formattedDate)} onClick={() => handleJournalModalOpen(formattedDate)} />
              )}
              <IconWithBg
                icon={<PlusCircleOutlined />}
                onClick={isPastDay ? undefined : handleAddBooking}
                className={`${styles.addIcon} ${isPastDay ? styles.addIconDisabled : ""}`}
              />
            </Flex>
          )}
        </Flex>

        {renderList?.length ? (
          <Flex vertical className="z-1 mt-3xl" gap={spacings.M}>
            {renderList.slice(0, 4)}
            {renderList.length > 4 && (
              <Button
                type="link"
                onClick={() => onChangeFilters({ target: { name: "range", value: "date" }, extra: { name: "date", value: formattedDate } })}
              >
                {t("calendar.view_more")}
              </Button>
            )}
          </Flex>
        ) : null}
      </div>
    </Tooltip>
  )
}

TimeOffTooltip.propTypes = {
  timeOffRanges: PropTypes.arrayOf(PropTypes.string),
  formattedDate: PropTypes.string.isRequired,
  journalNoteDates: PropTypes.arrayOf(PropTypes.string),
  handleJournalModalOpen: PropTypes.func.isRequired,
  handleAddBooking: PropTypes.func.isRequired,
  day: PropTypes.instanceOf(moment).isRequired,
  renderList: PropTypes.arrayOf(PropTypes.node).isRequired,
  onChangeFilters: PropTypes.func.isRequired
}

TimeOffTooltip.displayName = "TimeOffTooltip"

export default memo(TimeOffTooltip)
